import React, { useState, useEffect } from 'react';
import { DataTable } from 'primereact/datatable';
import { Button } from 'primereact/button';
import { Column } from 'primereact/column';
import { Link } from 'react-router-dom';
import Layout from '../component/layout';
import { makeRequest2 } from '../../../utils/makeRequest';
import { toastify } from '../../../utils/toast';
import { getItem } from '../../../utils/localStorage';
import { getPropertyManagedUnitsURL, getPropertyManagerContractsURL } from '../../../utils/urls';

// Import the contract details dialog
import PropertyManagerContractDetailsDialog from './contracts/PropertyManagerContractDetails';

const PropertyManagedUnits = () => {
    const [propertyManagedUnits, setPropertyManagedUnits] = useState([]);
    const [filteredUnits, setFilteredUnits] = useState([]);
    const [contracts, setContracts] = useState([]);
    const [loading, setLoading] = useState(false);
    const [searchTerm, setSearchTerm] = useState('');
    
    // Dialog states for contract details
    const [showContractDialog, setShowContractDialog] = useState(false);
    const [selectedContract, setSelectedContract] = useState(null);

    // Function to get contract for a unit
    const getContractForUnit = (unitId) => {
        return contracts.find(contract => 
            contract.unitDetails && 
            contract.unitDetails.some(unit => unit._id === unitId || unit.unitId === unitId)
        );
    };

    const getPropertyManagedUnits = async () => {
        try {
            setLoading(true);
            const facilityId = await getItem('selectedFacilityId');
            
            // Fetch both units and contracts
            const [unitsResponse, contractsResponse] = await Promise.all([
                makeRequest2(getPropertyManagedUnitsURL + '/' + facilityId, 'GET'),
                makeRequest2(getPropertyManagerContractsURL + '/' + facilityId, 'GET')
            ]);

            // Process units data
            if (unitsResponse.success) {
                const { propertyManagedUnits } = unitsResponse.data;
                setPropertyManagedUnits(propertyManagedUnits);
                setFilteredUnits(propertyManagedUnits);
            } else {
                throw new Error(unitsResponse.error);
            }

            // Process contracts data
            if (contractsResponse?.success && contractsResponse.data) {
                let contractsData = null;
                
                if (contractsResponse.data.data && contractsResponse.data.data.contracts) {
                    contractsData = contractsResponse.data.data.contracts;
                } else if (contractsResponse.data.contracts) {
                    contractsData = contractsResponse.data.contracts;
                } else if (Array.isArray(contractsResponse.data)) {
                    contractsData = contractsResponse.data;
                }

                setContracts(contractsData || []);
            } else {
                setContracts([]);
            }

        } catch (err) {
            toastify(err.message, 'error');
            setPropertyManagedUnits([]);
            setFilteredUnits([]);
            setContracts([]);
        } finally {
            setLoading(false);
        }
    };

    // Search functionality
    const handleSearch = (value) => {
        setSearchTerm(value);
        
        if (value === '') {
            setFilteredUnits(propertyManagedUnits);
        } else {
            const filtered = propertyManagedUnits.filter(unit => {
                const unitName = unit.name?.toLowerCase() || '';
                const unitType = unit.unitType?.toLowerCase() || '';
                const clientName = unit.homeOwner?.fullName?.toLowerCase() || '';
                const searchValue = value.toLowerCase();
                
                return unitName.includes(searchValue) || 
                       unitType.includes(searchValue) || 
                       clientName.includes(searchValue);
            });
            
            setFilteredUnits(filtered);
        }
    };

    // Template for unit name with link
    const unitNameTemplate = (rowData) => {
        return (
            <Link
                to={`/facility/unit_management/view_unit/${rowData._id}`}
                className="text-blue-600 hover:text-blue-800 no-underline font-bold"
            >
                {rowData.name}
            </Link>
        );
    };

    // Template for contract status
    const contractStatusTemplate = (rowData) => {
        const contract = getContractForUnit(rowData._id);
        const status = contract ? contract.status : 'No Contract';
        
        const getStatusClasses = (status) => {
            switch (status.toLowerCase()) {
                case 'active':
                    return 'bg-green-100 text-green-800';
                case 'inactive':
                    return 'bg-orange-100 text-orange-800';
                case 'completed':
                    return 'bg-blue-100 text-blue-800';
                case 'suspended':
                    return 'bg-yellow-100 text-yellow-800';
                case 'terminated':
                    return 'bg-red-100 text-red-800';
                default:
                    return 'bg-gray-100 text-gray-800';
            }
        };

        const statusClasses = getStatusClasses(status);

        return (
            <span className={`px-3 py-1 rounded-full text-sm font-medium inline-block ${statusClasses}`}>
                {status}
            </span>
        );
    };

    // Template for client (home owner)
    const clientTemplate = (rowData) => {
        if (rowData.homeOwner) {
            return (
                <span className="font-medium">
                    {rowData.homeOwner.fullName}
                </span>
            );
        }
        return <span className="text-gray-500">No client assigned</span>;
    };

    // Template for actions - contract view popup
    const actionTemplate = (rowData) => {
        const contract = getContractForUnit(rowData._id);
        
        if (!contract) {
            return (
                <div className="flex gap-2">
                    <span className="text-gray-500 text-sm">No Contract</span>
                </div>
            );
        }

        return (
            <div className="flex gap-2">
                <button
                    className="inline-flex items-center justify-center px-2 py-1 text-sm bg-green-600 text-white rounded hover:bg-green-700 transition-colors"
                    title="View Contract"
                    onClick={() => handleViewContract(contract)}
                >
                    <i className="ti ti-file-text"></i>
                </button>
            </div>
        );
    };

    // Handle view contract popup
    const handleViewContract = (contract) => {
        setSelectedContract(contract);
        setShowContractDialog(true);
    };

    const getCurrentData = () => {
        return filteredUnits;
    };

    useEffect(() => {
        getPropertyManagedUnits();
    }, []);

    return (
        <Layout>
            <div className="page-header">
                <div className="page-block">
                    <div className="row align-items-center">
                        <div className="col-md-12">
                            <ul className="breadcrumb">
                                <li className="breadcrumb-item">
                                    <Link to={"/core/dashboard/"}>Dashboard</Link>
                                </li>
                                <li className="breadcrumb-item">
                                    <Link to={"#"}>Property Management</Link>
                                </li>
                                <li className="breadcrumb-item">
                                    <Link to={"#"}>Managed Apartments</Link>
                                </li>
                            </ul>
                        </div>
                    </div>
                </div>
            </div>

            <div className="row">
                <div className="col-sm-12">
                    {/* Main Content Card */}
                    <div className="card">
                        <div className="card-header d-flex justify-content-between align-items-center">
                            <h5 className="mb-0">Property Managed Apartments</h5>
                            <Button 
                                label="Refresh" 
                                icon="pi pi-refresh" 
                                className="p-button-outlined"
                                onClick={getPropertyManagedUnits}
                                loading={loading}
                            />
                        </div>
                        
                        <div className="card-body">
                            {/* Search Box */}
                            <div className="row mb-4">
                                <div className="col-md-6">
                                    <div className="form-search">
                                        <i className="ti ti-search"></i>
                                        <input 
                                            type="search" 
                                            className="form-control" 
                                            placeholder="Search apartments or clients..."
                                            value={searchTerm}
                                            onChange={(e) => handleSearch(e.target.value)}
                                        />
                                    </div>
                                </div>
                                <div className="col-md-6 text-end">
                                    <span className="text-muted">
                                        Showing {filteredUnits.length} of {propertyManagedUnits.length} apartments
                                    </span>
                                </div>
                            </div>

                            {loading ? (
                                <div className="text-center p-6">
                                    <i className="ti ti-loader animate-spin text-blue-600 text-3xl"></i>
                                    <p className="mt-2">Loading property managed apartments...</p>
                                </div>
                            ) : (
                                <div className="dt-responsive table-responsive">
                                    <DataTable
                                        value={getCurrentData()}
                                        emptyMessage="No property managed apartments found."
                                        sortMode="multiple"
                                        paginator
                                        rows={10}
                                        stripedRows
                                        tableStyle={{ minWidth: '50rem' }}
                                        paginatorTemplate="FirstPageLink PrevPageLink PageLinks NextPageLink LastPageLink CurrentPageReport RowsPerPageDropdown"
                                        currentPageReportTemplate="Showing {first} to {last} of {totalRecords} apartments"
                                        rowsPerPageOptions={[10, 15, 25]}
                                    >
                                        <Column 
                                            header="Apartment Name" 
                                            body={unitNameTemplate}
                                            sortable 
                                            field="name"
                                        />
                                        <Column 
                                            field="unitType" 
                                            header="Apartment Type" 
                                            sortable 
                                        />
                                        <Column 
                                            header="Contract Status" 
                                            body={contractStatusTemplate} 
                                            sortable 
                                        />
                                        <Column 
                                            header="Client" 
                                            body={clientTemplate} 
                                        />
                                        <Column 
                                            header="Actions" 
                                            body={actionTemplate} 
                                            style={{ width: '100px' }}
                                        />
                                    </DataTable>
                                </div>
                            )}
                        </div>
                    </div>
                </div>
            </div>

            {/* Contract Details Dialog */}
            <PropertyManagerContractDetailsDialog
                visible={showContractDialog}
                onHide={() => setShowContractDialog(false)}
                contractData={selectedContract}
                contractId={selectedContract?._id}
            />
        </Layout>
    );
};

export default PropertyManagedUnits;